import config from "@/config";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { io } from "socket.io-client";
import ProcessingScreen from "@/b2b/pages/processing";
import { partners } from "@/b2b/config/partnerJourney";
import { fetchData } from "@/lib/helper";

const ProcessDisbursal = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [responseData, setResponseData] = useState(null);
  const [latestNavigateURL, setLatestNavigateURL] = useState(null); // To store socket URL
  const [journey, setJourney] = useState(null);

  const socket = useRef(null);
  const timeoutRef = useRef(null); // Track timeout
  const navigateURLRef = useRef("onboarding");
  const kycRedirectDone = useRef(false);

  const userIdentifier = localStorage.getItem("user_identifier");
  const partner = localStorage.getItem("partnerIdentifier");

  const queryParams = new URLSearchParams(location.search);
  const sourceParam = queryParams.get("source");
  const statusParam = queryParams.get("status");

  console.log("Latest URL: ", latestNavigateURL);

  useEffect(() => {
    const fetchJourneyData = async () => {
      try {
        const data = await fetchData("journey/full");
        setJourney(data.journey);
        if (sourceParam === "mandate") {
          if (data?.journey.agreementLink) {
            window.location.href = data?.journey.agreementLink;
            console.log(
              "sourceParam: ",
              sourceParam,
              data?.journey.agreementLink
            );
          }
        }
        console.log(("Fetched journey data:", data?.journey));
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchJourneyData();

    if (partner) {
      const newNavigateURL = `/b/${userIdentifier}?partner=${partner}&step=redirect`;
      console.log("Updated Navigate URL:", newNavigateURL);
      navigateURLRef.current = newNavigateURL; // Store latest URL in `useRef`
    }
  }, [partner]);
  const routeAfterKyc = async () => {
    try {
      const stepsRes = await fetchData("journey");
      const steps = stepsRes?.journey || [];
      const find = (name, actor) =>
        steps.find((s) => s.actor === actor && s.name === name);
      // Banking done + penny-drop pending → go straight to penny-drop.
      if (
        find("BANKING", "USER")?.status === "SUCCESS" &&
        find("PENNY_DROP", "SYSTEM")?.status === "PENDING"
      ) {
        return navigate("/penny-drop");
      }
      navigate("onboarding");
    } catch (e) {
      console.error("[kyc] routeAfterKyc failed, falling back to onboarding", e);
      navigate("onboarding");
    }
  };

  useEffect(() => {
    if (sourceParam !== "kyc" && sourceParam !== "ekyc") return;
    if (partner) return; // partner flow keeps the original socket routing
    if (kycRedirectDone.current) return;
    kycRedirectDone.current = true;
    routeAfterKyc();
  }, [sourceParam, partner]);

  useEffect(() => {
    if (latestNavigateURL) {
      const cleanedNavigate = latestNavigateURL.replace(/^\/+/, "");

      const redirectURL = partners.includes(partner)
        ? `/b/${userIdentifier}?partner=${partner}&step=${cleanedNavigate}`
        : navigate("onboarding");

      navigate(redirectURL);
      clearTimeout(timeoutRef.current);
    }
  }, [latestNavigateURL, navigateURLRef]);

  useEffect(() => {
    if (sourceParam === "esign" && statusParam === "success") {
      console.log("Handling eSign Success via Query Params");
      setLatestNavigateURL("mandate");
    }
  }, [sourceParam, statusParam]);

  useEffect(() => {
    let timeoutId;

    // KYC (non-partner) is handled by the dedicated redirect effect above — skip
    // the socket/processing wait so the user goes straight to the next step.
    const srcParam = new URLSearchParams(location.search).get("source");
    if ((srcParam === "kyc" || srcParam === "ekyc") && !partner) return;

    const queryParams = new URLSearchParams(location.search);
    if (queryParams) {
      const source = queryParams.get("source");
      if (source === "mandate" && journey !== null) {
        if (journey?.agreementLink) {
          console.log("Redirecting to agreementLink:", journey.agreementLink);
          window.location.href = journey.agreementLink;
          return; // Exit early to prevent further processing
        } else {
          console.log("No agreementLink found, redirecting to happy-move-in");
          const redirectURL = partners.includes(partner)
            ? `/b/${userIdentifier}?partner=${partner}&step=happy-move-in`
            : "/home";
          navigate(redirectURL);
          return; // Exit early to prevent further processing
        }
      }
    }

    if (!socket.current) {
      const searchParams = new URLSearchParams(location.search);
      const source = searchParams.get("source");
      const bankiingRefId = searchParams.get("refId");
      localStorage.setItem("bankingRefId", bankiingRefId);
      console.log("Source from URL:", source);
      const token = localStorage.getItem(config.LOCAL_KEYS.SIGNUP_TOKEN);

      // Initialize Socket.IO connection
      socket.current = io(`${config.API_URL}`, {
        transports: ["websocket"],
        query: { token },
      });

      socket.current.on("connect", () => {
        console.log("Socket.IO connection established");
        socket.current.emit("joinRoom", { token });

        // if (source) {
        //   socket.current.emit("init", { source });
        //   console.log("Initialization message sent with source:", source);
        // }
      });

      socket.current.on("newData", (newData) => {
        console.log("New data received from server:", newData);

        if (newData?.data?.status) {
          // if (source === "banking" && newData?.data?.source === "banking") {
          if (newData?.data?.source === "banking") {
            if (partner) {
              if (newData?.data?.status === "success") {
                setLatestNavigateURL("congrats");
              } else {
                setLatestNavigateURL("banking-sorry");
              }
            } else {
              // setLatestNavigateURL(newData?.data.navigate);
              if (newData?.data?.status === "success") {
                setLatestNavigateURL("congrats");
              } else {
                setLatestNavigateURL("banking-sorry");
              }
            }
          } else if (
            newData?.data?.source === "kyc" ||
            newData?.data?.source === "ekyc"
          ) {
            setLatestNavigateURL("onboarding");
          } else if (newData?.data?.source === "esign") {
            setLatestNavigateURL("mandate");
          } else if (newData?.data?.source === "mandate") {
            const agreementLink = newData?.data?.agreementLink || journey?.agreementLink;
            if (agreementLink) {
              console.log("Mandate success - redirecting to agreementLink:", agreementLink);
              window.location.href = agreementLink;
            } else {
              console.log("Mandate success - no agreementLink, going to happy-move-in");
              setLatestNavigateURL("happy-move-in");
            }
          } else if (newData?.data?.source === "rent")
            setLatestNavigateURL("happy-move-in");
        } else if (sourceParam === "esign") {
          setLatestNavigateURL("mandate");
        }
      });

      socket.current.on("joined", (response) => {
        console.log("Joined room:", response);
      });

      socket.current.on("connect_error", (error) => {
        console.log("Socket.IO connection error:", error);
      });

      socket.current.on("disconnect", () => {
        console.log("Socket.IO connection closed");
      });

      timeoutId = setTimeout(() => {
        console.log("Timeout: Redirecting to homepage.");
        console.log("Navigating to:", navigateURLRef.current);
        navigate(`/b/${userIdentifier}?partner=${partner}&step=onboarding`);
      }, 20000);

      timeoutRef.current = timeoutId;
    }

    return () => {
      if (socket.current) {
        socket.current.disconnect();
        socket.current = null;
        console.log("Socket.IO connection closed");
      }
      clearTimeout(timeoutRef.current);
    };
  }, [location, navigate, journey]);

  const isSuccess = responseData?.status === "success";

  // 🔴 Do NOT add `recoverable` here. Unlike the other wait screens this one is not waiting on a
  // request that either answers or does not — it is waiting on a socket event for a backend process
  // that legitimately runs for minutes. A "taking longer than usual" prompt would fire during
  // perfectly healthy operation, and reloading mid-wait risks missing the very event it is here to
  // receive.
  return <ProcessingScreen timer />;
};

export default ProcessDisbursal;
